import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AdministrarEmpleadosFacade } from '../services/administrar-empleados.facade';
import { TablaEmpleadosComponent } from '../components/tabla-empleados/tabla-empleados.component';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';
import { BusquedaUnidadAdministrativaDialogComponent, UnidadAdministrativaSeleccionada } from 'src/app/presentation/shared/buscadores/buscador-unidad-administrativa-dialog.component';
import { ModificarEmpleadoComponent } from 'src/app/presentation/empleados/modificar-empleado/container/modificar-empleado.component';
import { CrearEmpleadoComponent } from 'src/app/presentation/empleados/crear-empleado/container/crear-empleado.component';
import { EmpleadoResult } from 'src/app/domain/model/empleado/empleado';
import { sanitizeText } from 'src/app/common/input-utils';
import { TIPO_NIT } from 'src/app/domain/model/persona/persona';

/**
 * Container (smart component) de "Administrar Empleados/Consultores" (HU1259115).
 *
 * <p>Solo orquesta UI y delega al {@link AdministrarEmpleadosFacade} — sin
 * lógica de negocio propia (frontend-girop §14). Implementa RN-01: dos modos
 * de búsqueda (por identificación / por UEN), paginación cursor manual
 * (frontend-girop §17 — nunca `[paginator]="true"`).</p>
 *
 * <p>Legacy: AdminEmpleadosActivity (listado). El botón "Editar" abre
 * {@link ModificarEmpleadoComponent} en un `p-dialog` (fase 19, mismo patrón
 * de modal-edición que Servicios Entregados) en vez de navegar a una ruta
 * propia — el formulario de Crear sigue sin implementar.</p>
 */
@Component({
  selector: 'app-administrar-empleados',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FieldsetModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    DropdownModule,
    RadioButtonModule,
    ProgressSpinnerModule,
    TablaEmpleadosComponent,
    BusquedaPaginadorComponent,
    BusquedaUnidadAdministrativaDialogComponent,
    DialogModule,
    ModificarEmpleadoComponent,
    CrearEmpleadoComponent,
    ToastModule,
  ],
  providers: [AdministrarEmpleadosFacade, MessageService],
  template: `
    <p-toast></p-toast>
    <div class="p-3 sm:p-4 lg:p-6 max-w-7xl mx-auto overflow-x-hidden">
      <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h1 class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-gray-100">
          👤 Administrar Empleados/Consultores
        </h1>
        <p-button label="Nuevo" icon="pi pi-plus" styleClass="min-h-[44px]" (onClick)="nuevo()"></p-button>
      </div>

      <p-fieldset legend="Filtros Consulta de Empleados" [toggleable]="true" styleClass="mb-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3 mb-3">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo identificación</label>
            <p-dropdown [(ngModel)]="facade.filtro.tipoId" [options]="facade.tiposIdentificacion()"
              optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
              [filter]="true" filterBy="descripcion" [showClear]="true" appendTo="body"
              [style]="{ width: '100%' }"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Número identificación</label>
            <input pInputText [(ngModel)]="facade.filtro.numId" class="w-full" maxlength="20" />
          </div>

          <!-- Campos Persona Jurídica (NIT) -->
          @if (isFiltroPersonaJuridica()) {
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre comercial</label>
              <input pInputText [(ngModel)]="facade.filtro.nombreComercial" class="w-full" maxlength="100"
                (input)="facade.filtro.nombreComercial = sanitizeText(facade.filtro.nombreComercial)" />
            </div>
          }

          <!-- Campos Persona Natural (CC, CE, etc.) -->
          @if (!isFiltroPersonaJuridica()) {
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primer nombre</label>
              <input pInputText [(ngModel)]="facade.filtro.nombre1" class="w-full" maxlength="30"
                (input)="facade.filtro.nombre1 = sanitizeText(facade.filtro.nombre1)" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Segundo nombre</label>
              <input pInputText [(ngModel)]="facade.filtro.nombre2" class="w-full" maxlength="30"
                (input)="facade.filtro.nombre2 = sanitizeText(facade.filtro.nombre2)" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Primer apellido</label>
              <input pInputText [(ngModel)]="facade.filtro.apellido1" class="w-full" maxlength="30"
                (input)="facade.filtro.apellido1 = sanitizeText(facade.filtro.apellido1)" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Segundo apellido</label>
              <input pInputText [(ngModel)]="facade.filtro.apellido2" class="w-full" maxlength="30"
                (input)="facade.filtro.apellido2 = sanitizeText(facade.filtro.apellido2)" />
            </div>
          }

          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo empleado</label>
            <p-dropdown [(ngModel)]="facade.filtro.cdtipoEmpleado" [options]="facade.tiposEmpleado()"
              optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
              [filter]="true" filterBy="descripcion" [showClear]="true" appendTo="body"
              [style]="{ width: '100%' }"></p-dropdown>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Activo</label>
            <div class="flex flex-wrap gap-4 pt-2">
              <div class="flex items-center">
                <p-radioButton name="activo" value="S" [(ngModel)]="facade.filtro.activo" inputId="activoSi"></p-radioButton>
                <label for="activoSi" class="ml-2 text-sm">Sí</label>
              </div>
              <div class="flex items-center">
                <p-radioButton name="activo" value="N" [(ngModel)]="facade.filtro.activo" inputId="activoNo"></p-radioButton>
                <label for="activoNo" class="ml-2 text-sm">No</label>
              </div>
              <div class="flex items-center">
                <p-radioButton name="activo" value="" [(ngModel)]="facade.filtro.activo" inputId="activoTodos"></p-radioButton>
                <label for="activoTodos" class="ml-2 text-sm">Todos</label>
              </div>
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Oficina</label>
            <p-dropdown [(ngModel)]="facade.filtro.nmconsecOficina" [options]="facade.oficinas()"
              optionLabel="descripcion" optionValue="codigo" placeholder="--Seleccione--"
              [filter]="true" filterBy="descripcion" [showClear]="true" appendTo="body"
              [style]="{ width: '100%' }"></p-dropdown>
          </div>

          <div class="sm:col-span-2 lg:col-span-3">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Unidad administrativa</label>
            <div class="flex gap-1 min-w-0">
              <input pInputText [value]="facade.uenDisplay()" readonly class="flex-1 min-w-0" placeholder="Seleccione una UEN" />
              <button pButton icon="pi pi-search" class="p-button-outlined p-button-sm flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Buscar UEN" (click)="dialogUenVisible = true"></button>
              <button pButton icon="pi pi-times" class="p-button-text p-button-danger flex-shrink-0 min-w-[44px] min-h-[44px]"
                pTooltip="Limpiar" (click)="facade.limpiarUen()" [disabled]="!facade.uenDisplay()"></button>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 sm:gap-3 justify-end sm:justify-start w-full">
          <p-button label="Limpiar" icon="pi pi-eraser" severity="secondary" [outlined]="true"
            styleClass="min-h-[44px]" (onClick)="facade.limpiarFiltros()"></p-button>
          <p-button label="Buscar" icon="pi pi-search" styleClass="min-h-[44px]"
            [loading]="facade.loading()" (onClick)="facade.buscar()"></p-button>
        </div>
      </p-fieldset>

      @if (facade.mensaje()) {
        <div class="p-3 mb-4 rounded"
          [ngClass]="{
            'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200': facade.mensajeTipo() === 'warning',
            'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200': facade.mensajeTipo() === 'error',
            'bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200': facade.mensajeTipo() === 'info'
          }">
          {{ facade.mensaje() }}
        </div>
      }

      @if (facade.loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '50px', height: '50px' }"></p-progressSpinner>
        </div>
      }

      @if (facade.busquedaRealizada() && !facade.loading()) {
        <p-card styleClass="mb-4">
          <ng-template pTemplate="header">
            <div class="p-3 pb-0 flex justify-between items-center flex-wrap gap-2">
              <h3 class="text-lg font-semibold text-gray-700 dark:text-gray-200 m-0">Listado de Empleados</h3>
              @if (facade.totalElements() > 0) {
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  Registros del {{ facade.registroInicio() }} hasta el {{ facade.registroFin() }} de {{ facade.totalElements() }}
                </span>
              }
            </div>
          </ng-template>

          <app-tabla-empleados
            [items]="facade.items()"
            [loading]="facade.loading()"
            (editar)="editar($event)">
          </app-tabla-empleados>

          @if (facade.totalElements() > 0) {
            <app-busqueda-paginador
              [currentPage]="facade.currentPage()"
              [totalPages]="facade.totalPages()"
              [hasPrevious]="facade.hasPrevious()"
              [hasNext]="facade.hasNext()"
              [pageSizeOptions]="facade.pageSizeOptions"
              [selectedPageSize]="facade.pageSize()"
              (pageSizeChange)="facade.cambiarTamanioPagina($event)"
              (first)="facade.irPrimeraPagina()"
              (previous)="facade.irPaginaAnterior()"
              (next)="facade.irPaginaSiguiente()"
              (last)="facade.irUltimaPagina()">
            </app-busqueda-paginador>
          }
        </p-card>
      }
    </div>

    @if (dialogUenVisible) {
      <app-busqueda-unidad-administrativa-dialog
        (unidadAdministrativaSelected)="onUenSeleccionada($event)"
        (dialogClosed)="dialogUenVisible = false">
      </app-busqueda-unidad-administrativa-dialog>
    }

    <p-dialog
      header="Modificar Empleado/Consultor"
      [visible]="mostrarModalEditar()"
      (visibleChange)="mostrarModalEditar.set($event)"
      (onHide)="cerrarModalEditar()"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh' }"
      [contentStyle]="{ height: 'calc(100vh - 6rem)', 'overflow-y': 'auto' }">
      @if (mostrarModalEditar()) {
        <app-modificar-empleado
          [nmconsecutivo]="nmconsecutivoEditar()"
          (guardado)="onEmpleadoGuardado()"
          (cancelado)="cerrarModalEditar()">
        </app-modificar-empleado>
      }
    </p-dialog>

    <p-dialog
      header="Nuevo Empleado/Consultor"
      [visible]="mostrarModalCrear()"
      (visibleChange)="mostrarModalCrear.set($event)"
      (onHide)="cerrarModalCrear()"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh' }"
      [contentStyle]="{ height: 'calc(100vh - 6rem)', 'overflow-y': 'auto' }">
      @if (mostrarModalCrear()) {
        <app-crear-empleado
          [abrir]="aperturaCrear()"
          (guardado)="onEmpleadoCreado()"
          (cancelado)="cerrarModalCrear()">
        </app-crear-empleado>
      }
    </p-dialog>
  `
})
export class AdministrarEmpleadosComponent implements OnInit {
  protected readonly facade = inject(AdministrarEmpleadosFacade);
  private readonly messageService = inject(MessageService);
  protected readonly sanitizeText = sanitizeText;

  dialogUenVisible = false;

  /** Modal de Modificar (fase 19, mismo patrón que Servicios Entregados: p-dialog en el listado, sin ruta propia). */
  readonly mostrarModalEditar = signal(false);
  readonly nmconsecutivoEditar = signal<number | null>(null);

  /** Modal de Crear (mismo patrón de modal embebido que Modificar). */
  readonly mostrarModalCrear = signal(false);
  /** Se incrementa en cada apertura para que `CrearEmpleadoComponent.ngOnChanges` reinicie el formulario, incluso si se abre dos veces seguidas. */
  readonly aperturaCrear = signal(0);

  ngOnInit(): void {
    this.facade.cargarCatalogos();
  }

  onUenSeleccionada(uen: UnidadAdministrativaSeleccionada): void {
    this.facade.seleccionarUen(uen.nmconsecutivo, uen.dsunidadAdministrativa);
    this.dialogUenVisible = false;
  }

  /**
   * Legacy: AdminEmpleadosViewImpl.mostrarOcultarCampos() — con tipo NIT se
   * filtra por Nombre Comercial (persona jurídica); con cualquier otro tipo
   * (CC, CE, ...) se filtra por nombre1/nombre2/apellido1/apellido2 (persona
   * natural). Mismo criterio ya usado en personas-list.component.ts.
   */
  isFiltroPersonaJuridica(): boolean {
    return this.facade.filtro.tipoId === TIPO_NIT;
  }

  editar(item: EmpleadoResult): void {
    this.nmconsecutivoEditar.set(item.nmconsecutivo);
    this.mostrarModalEditar.set(true);
  }

  cerrarModalEditar(): void {
    this.mostrarModalEditar.set(false);
    this.nmconsecutivoEditar.set(null);
  }

  nuevo(): void {
    this.aperturaCrear.update((n) => n + 1);
    this.mostrarModalCrear.set(true);
  }

  cerrarModalCrear(): void {
    this.mostrarModalCrear.set(false);
  }

  /**
   * Legacy: `CrearEmpleadoActivity.crearEmpleado()` → `onResponseOk` (rama de
   * éxito) — `cons.ingresoExitoso()` = "Los datos se almacenaron
   * correctamente." (`WidgetsNamesConstants.properties:80`), verificado
   * literal, no asumido por analogía con el mensaje de Modificar (que es
   * distinto: `cons.actualizacionExitosa()`).
   *
   * <p>Diferencia deliberada frente al legado: `CrearEmpleadoActivity`
   * navega a `ModificarEmpleadoPlace` con el empleado recién creado (para
   * seguir editándolo); aquí se regresa al listado y se refresca, igual que
   * ya hace Modificar — mismo patrón de modal-embebido-en-listado en toda la
   * pantalla, sin una ruta propia de Crear/Modificar a la que "navegar".</p>
   */
  async onEmpleadoCreado(): Promise<void> {
    this.cerrarModalCrear();
    await this.facade.buscar();
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: 'Los datos se almacenaron correctamente.',
      life: 3000,
    });
  }

  /**
   * Legacy: `ModificarEmpleadoActivity.modificarEmpleado()` — al confirmar
   * éxito (`genericoRespuesta.getExito()`) muestra
   * `cons.actualizacionExitosa()` = "Los datos se actualizaron
   * correctamente." (no `cons.successfulProcess()` = "El proceso se ha
   * realizado con éxito" — ese es el texto de otras pantallas de patrón
   * similar, como `ModificarEncabezadoFacturaActivity`, pero no el de esta;
   * verificado literal contra `WidgetsNamesConstants.properties`, no
   * asumido por analogía) y regresa al listado.
   *
   * <p>La confirmación se muestra con `p-toast` (flotante, se cierra solo a
   * los 3s — pedido explícito del usuario) desde este componente, no desde
   * el modal: `ModificarEmpleadoFacade` se provee a nivel del propio modal
   * y se destruye junto con el diálogo al cerrarse, así que un mensaje
   * puesto en `guardar()` nunca llegaría a verse. `MessageService` se provee
   * aquí (frontend-girop §10) porque no hay ningún `p-toast` en un layout
   * ancestro.</p>
   */
  async onEmpleadoGuardado(): Promise<void> {
    this.cerrarModalEditar();
    await this.facade.buscar();
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: 'Los datos se actualizaron correctamente.',
      life: 3000,
    });
  }
}
